/**
 * ActionBar.tsx —— 行动区（PRD 模块 1 / 设计方案 4.5、5.5）
 *
 * 规则来源只有一个：引擎的 `legalActionsFor()`。UI 不自行推导任何合法性，
 * 只负责把 min/max（to 语义）映射成滑杆与快捷档，并在提交前做一次二次确认。
 *
 * 状态穷举：不可行动（AI 回合 / 动画中）→ 全部禁用；可行动 → 按 legal 逐项开关；
 * 提交后 600ms 内锁定防连点；超过筹码上限的输入自动收敛为全下。
 *
 * 布局要点（第 2 轮整改）：
 *   - 按钮行 `.actionbar__main` 永远留在文档流最底部，加注抽屉与二次确认气泡
 *     都是锚定在它上沿之上的浮层（`.actionbar__dock` 提供包含块），
 *     因此浮层再高也压不到行动热区、也不会把页脚顶出视口（BLK-1 / BLK-2）。
 *   - 四个主行动（弃牌 / 跟注·过牌 / 加注 / 全下）在**所有断点**都留在按钮行里，
 *     窄屏只压缩尺寸与文案排布，不做入口迁移（MAJ-F4，见下方 `allInButton` 处注释）。
 */

import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Action, LegalActions } from '../core/betting';
import type { Card } from '../core/cards';
import { chips, pct } from '../state/format';
import type { Settings } from '../state/settings';
import { ConfirmPopover, PlayingCard } from './primitives';
import { overlayOpen } from './overlay';
import { useDismissable } from './hooks';
import type { SizerMode } from './overlayPlacement';

export interface ActionBarProps {
  legal: LegalActions | null;
  /** 是否轮到玩家且可提交（false 时整区禁用）。 */
  active: boolean;
  disabledReason: string;
  pot: number;
  myStack: number;
  myStreetContributed: number;
  bigBlind: number;
  settings: Settings;
  clockRemainingMs: number | null;
  /**
   * BLK-F1：加注抽屉的承载模式，由 `GameScreen` 按**实测可用带宽 + 视口高**分级求解后传入
   * （见 `overlayPlacement.pickSizerMode`）。
   *
   * R14-1 后模式**只决定排版疏密，不再决定滑杆/输入是否存在**：`full` / `compact` / `short`
   * 三档都恒渲染「快捷档 + 滑杆 + 数字输入 + 确认」四件，短高度靠浮层内部滚动兜底。
   * 移动端（stack 竖/横屏两目标）承载下限已被 `pickSizerMode(mobileFloor)` 封顶在 `compact`，
   * 因此两目标尺寸下 `data-sizer-mode ∈ {full, compact}`、绝不落无滑杆的旧 `short`。
   *
   * 为什么模式由外层给、而不是这里自己读 `window.innerHeight`：抽屉能不能装下滑杆
   * 取决于「受保护读数区下沿 → 席位卡上沿」这条**动态**带宽（人数档 / 是否贴盲注都会变），
   * 只有测量方（GameScreen 的 effect）知道真实值。这里只负责按模式渲染。
   */
  sizerMode?: SizerMode;
  /** 加注抽屉开合回调：外层据此让赢率浮层收成把手，避免两层浮层互相叠压。 */
  onSizerOpenChange?: (open: boolean) => void;
  /**
   * DEF-02（Round2 阻断修复 · 层级）：加注抽屉展开态的 portal 承载根（窄屏 = `.gs__overlay`）。
   *
   * 抽屉是 `.actionbar__dock` 内 `position:absolute` 的浮层，而 `.gs__foot` 是 isolate + z:20
   * 的层叠上下文——抽屉再高也被封顶在根上下文 z=20，低于公共牌带 `--z-board:40`。360×640 上
   * 抽屉浮到英雄区后，公共牌 / 我的席位卡 / 毛毡 / 气泡从上方「打穿」CTA（elementFromPoint 0/5）。
   * 传入根节点时，抽屉 portal 到 overlay 根（平面 C，有效 z --z-eqfloat:110 > 40），只提层不改几何，
   * 底边改由实测 `--sizer-open-bottom` 相对视口底定位。为 null（宽屏 / 根未挂载）时回退页脚内联渲染。
   */
  sizerPortalRoot?: HTMLElement | null;
  /**
   * Round26 · C3（问题1 · 方案 A）：竖屏窄带「既避按钮又避 Hero 手牌」无纯几何解时，
   * 由 GameScreen 置 true —— sizer 面板**顶部固定镜像一行 Hero 两张底牌**，此时面板可覆盖
   * 真实 Hero 手牌区，镜像保证「下注全程看得见自己的牌」（AC-B3 `visFrac=1`）。
   */
  heroMirror?: boolean;
  /** Round26 · C3：Hero 两张底牌（镜像行渲染源）；无牌 / 观战时 null，不渲染镜像。 */
  heroHole?: readonly Card[] | null;
  /**
   * R14-3：越轮认输是否武装（对局进行中 · 非我行动轮 · 我方未弃未出局 · 非发牌瞬时态）。
   * 为 true 时底部「弃牌」按钮**随时可点**——点击走越轮认输（置 `pendingConcede`），经二次确认。
   */
  concedeArmed?: boolean;
  /** R14-3：越轮认输落点（`controller.concedeAnytime`：置 pendingConcede、我方合法轮次自动 fold 落地）。 */
  onConcede?: () => void;
  /**
   * Round21 T6 / AC-S1：越轮弃牌意图是否已置位（`controller.pendingConcede`）。
   * 为 true 时底部按钮切换为「取消预选」——预选是**可取消**的（意图尚未在引擎落地），点击清除意图。
   */
  concedePending?: boolean;
  /** AC-S1：取消预选落点（`controller.cancelConcede`：清除尚未落地的 pendingConcede 意图）。 */
  onCancelConcede?: () => void;
  onSubmit: (a: Action) => void;
  /**
   * R13-2：弃牌后二选一推进浮层是否展开（由 controller.foldAdvancePrompt 驱动）。
   * 展开时**内联覆盖**按钮行 `.actionbar__main`（不是弹层），提供「快进本手 / 原速观战」二选一。
   */
  foldPrompt?: boolean;
  /** R13-2：二选一选择回调。remember=true 时写入 settings.foldAdvanceMode 并关闭「每次询问」。 */
  onFoldAdvance?: (mode: 'turbo' | 'normal', remember: boolean) => void;
}

/** 待确认动作 + 触发它的锚点按钮（用于气泡定位与关闭后的焦点归还）。 */
interface PendingConfirm {
  readonly action: Action;
  readonly anchorX: number | null;
  readonly anchorEl: HTMLElement | null;
}

/**
 * 行动按钮的 Tab 优先级（QA-BUG-006）。
 *
 * 缺陷现场：文档序是 topbar → 牌桌 → 赢率面板 → 页脚行动区，键盘用户实测要按 7 次 Tab
 * 才第一次停到行动按钮上；而行动区是本产品唯一有时限、且每手都要用的操作区。
 *
 * 这里刻意使用正 tabindex 把行动区提到序列最前：布局与视觉一号不动（批次 B 才碰布局），
 * 又能把「首达行动按钮」压到 1 次 Tab。顺序按「安全优先」排：
 * 跟注/过牌 → 加注 → 全下 → 弃牌，避免第一跳就停在不可逆的「弃牌」上。
 * 非我方回合时按钮全部 disabled，天然退出 Tab 序，不会抢走 topbar 的第一跳。
 */
const TAB_CALL = 1;
const TAB_AGGRO = 2;
const TAB_ALLIN = 3;
const TAB_FOLD = 4;
/** 加注抽屉内的控件紧随按钮行之后，保证「展开抽屉 → 继续 Tab」是连续的。 */
const TAB_SIZER = 5;

export function ActionBar(props: ActionBarProps) {
  const {
    legal,
    active,
    disabledReason,
    pot,
    myStack,
    myStreetContributed,
    bigBlind,
    settings,
    clockRemainingMs,
    sizerMode = 'full',
    onSizerOpenChange,
    sizerPortalRoot = null,
    heroMirror = false,
    heroHole = null,
    concedeArmed = false,
    onConcede,
    concedePending = false,
    onCancelConcede,
    onSubmit,
    foldPrompt = false,
    onFoldAdvance,
  } = props;

  const aggro = legal?.raise ?? legal?.bet ?? null;
  const isRaise = !!legal?.raise;
  const min = aggro?.min ?? 0;
  const max = aggro?.max ?? 0;
  const [amount, setAmount] = useState(min);
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);
  const [expanded, setExpanded] = useState(false);
  /** R14-3：越轮认输的二次确认气泡开合（锚定在「弃牌」按钮上方，防误触）。 */
  const [concedeConfirm, setConcedeConfirm] = useState(false);
  const dockRef = useRef<HTMLDivElement | null>(null);

  // 每次合法区间变化都把滑杆收敛回最小加注（避免沿用上一街的数值）
  useEffect(() => {
    setAmount(min);
    setExpanded(false);
  }, [min, max, pot]);

  const disabled = !active || !legal;
  const sizerOpen = !!aggro && expanded && !disabled;

  /*
   * R14-3：底部「弃牌」按钮的三态分流（语义唯一入口，顶栏认输已删）。
   *   - inTurnFold：轮到我且 fold 合法 → 走既有 fire({fold})，**无二次确认**（与历轮轮内弃牌一致）。
   *   - canConcede：非我行动轮但越轮认输已武装 → 点击弹二次确认，确认后走 controller.concedeAnytime
   *                （置 pendingConcede，我方合法轮次自动 fold 落地，结算逐字段等价，守 R13）。
   *   - 都不满足 → 禁用（未开局 / 发牌中 / 已弃 / 已置意图 / 观战 / 结算）。
   */
  const inTurnFold = !!(active && legal?.fold);
  const canConcede = !inTurnFold && concedeArmed;
  const foldClickable = inTurnFold || canConcede || concedePending;

  useEffect(() => {
    onSizerOpenChange?.(sizerOpen);
  }, [sizerOpen, onSizerOpenChange]);

  // R14-3：越轮认输一旦不再武装（轮到我 / 已落地 / 结算），残留的二次确认气泡自动收起。
  useEffect(() => {
    if (!canConcede && concedeConfirm) setConcedeConfirm(false);
  }, [canConcede, concedeConfirm]);

  const toCall = legal?.toCall ?? 0;
  const potAfterCall = pot + toCall;
  const quick = useMemo(() => {
    if (!aggro) return [];
    return settings.quickBets.map((ratio) => {
      const extra = Math.round(potAfterCall * ratio);
      const to = clamp(myStreetContributed + toCall + extra, min, max);
      return { ratio, to, ...ratioLabel(ratio) };
    });
  }, [aggro, settings.quickBets, potAfterCall, myStreetContributed, toCall, min, max]);

  /**
   * 二次确认判定（PRD 7.6）—— 点击与键盘共用这一个函数，不存在第二条判定路径。
   *
   * 三种「可能一步归零」的投入都必须拦一次：
   *   ① 显式全下；
   *   ② 跟注即全下（短筹码 call，引擎给出的 `call.allIn`）——QA-BUG-002 现场：
   *      改前只看 action.type，`{type:'call'}` 直接落到 return false，点击与键盘 C 都无气泡，
   *      筹码直接归零。按钮文案里的「（全下）」是提示，不是确认出口；
   *   ③ 下注 / 加注的本次实际投入 ≥ 剩余筹码 50%。
   */
  const needConfirm = (a: Action): boolean => {
    if (!settings.allInConfirm) return false;
    if (a.type === 'allin') return true;
    if (a.type === 'call') return !!legal?.call?.allIn;
    if ((a.type === 'bet' || a.type === 'raise') && a.amount !== undefined) {
      const extra = a.amount - myStreetContributed;
      return extra >= myStack * 0.5;
    }
    return false;
  };

  /** 锚点按钮中心相对按钮坞左边的偏移；拿不到布局信息时退回水平居中。 */
  const anchorXOf = (el: HTMLElement | null): number | null => {
    const dock = dockRef.current;
    if (!el || !dock) return null;
    const a = el.getBoundingClientRect();
    const d = dock.getBoundingClientRect();
    return a.left - d.left + a.width / 2;
  };

  /**
   * 键盘路径没有 currentTarget，用 `data-act` 反查按钮本体：
   * 气泡因此能锚定在正确位置，关闭后也能把焦点交还给这颗按钮（QA-BUG-020）。
   */
  const buttonFor = (a: Action): HTMLElement | null => {
    const kind = a.type === 'bet' ? 'raise' : a.type;
    return dockRef.current?.querySelector<HTMLElement>(`[data-act="${kind}"]`) ?? null;
  };

  const fire = (a: Action, anchor?: HTMLElement | null): void => {
    const el = anchor ?? buttonFor(a);
    if (needConfirm(a)) {
      setConfirm({ action: a, anchorX: anchorXOf(el), anchorEl: el });
      return;
    }
    onSubmit(a);
  };

  /*
   * 键盘快捷键（AC1.8）：F 弃牌 / C 跟注 / K 过牌 / R 加注 / A 全下 / ←→ 调整。
   *
   * 第一件事是闸门（QA-BUG-001）：只要有浮层挂着（二次确认气泡、设置 / 历史抽屉），
   * 所有动作键一律不生效。改前气泡打开时按 F 会真的弃牌，而且气泡还留在屏幕上，
   * 键盘用户完全没有绕过手段——这正是这条缺陷判「严重」的理由。
   * 气泡自身的 Esc / Tab / 确认按钮不走这里，由 useDismissable 与气泡内的按钮负责。
   */
  useEffect(() => {
    if (!active || !legal) return;
    const onKey = (e: KeyboardEvent): void => {
      if (confirm || overlayOpen()) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // QA-BUG-019：加注抽屉与其它浮层行为对齐，Esc 收起且不提交任何动作
      if (e.key === 'Escape') {
        if (!expanded) return;
        e.preventDefault();
        setExpanded(false);
        return;
      }
      const k = e.key.toLowerCase();
      if (k === 'f' && legal.fold) {
        e.preventDefault();
        fire({ type: 'fold' });
      } else if ((k === 'k' || (k === 'c' && legal.check)) && legal.check) {
        e.preventDefault();
        fire({ type: 'check' });
      } else if (k === 'c' && legal.call) {
        e.preventDefault();
        fire({ type: 'call' });
      } else if (k === 'r' || k === 'b') {
        if (!aggro) return;
        e.preventDefault();
        setExpanded(true);
      } else if (k === 'a' && legal.allIn) {
        e.preventDefault();
        fire({ type: 'allin' });
      } else if (aggro && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        const steps = e.shiftKey ? 10 : 1;
        const delta = (e.key === 'ArrowRight' ? bigBlind : -bigBlind) * steps;
        setAmount((v) => snapAmount(v + delta, min, max, bigBlind));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  /*
   * R14-3：非我行动轮的 F 键 —— 触发同一条越轮认输 + 二次确认路径（PRD §4.3）。
   *
   * 主动作键效应在 `!active || !legal` 时早返回、不响应；而越轮认输恰恰发生在非我轮，
   * 因此单独一条效应承接：仅在越轮认输已武装（`canConcede`）时挂载，按 F 弹二次确认气泡。
   * 其余动作键（跟注/加注/过牌/全下）仍受轮次限制，不在此响应。闸门与主效应一致
   * （有浮层 / 已在确认中 / 焦点在输入控件 / 组合键 → 让位）。
   */
  useEffect(() => {
    if (!canConcede) return;
    const onKey = (e: KeyboardEvent): void => {
      if (confirm || concedeConfirm || overlayOpen()) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setConcedeConfirm(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  /*
   * 轮到我行动时把焦点交给主行动按钮（QA-BUG-006 的决定性一环）。
   *
   * 只排 tabindex 是不够的：Chromium 的「顺序聚焦导航起点」一旦停在文档中段
   * （开局那次点击、上一手点过的任何控件都会留下起点），正 tabindex 组在扁平顺序里位于起点
   * **之前**，前向 Tab 会先走完 tabindex=0 组再绕回来 —— 真机实测仍需 7 次
   * （verify-tab-order.mjs 改前实测 7，与 QA 报告一致）。行动区是本产品唯一有 30s 时限的操作区，
   * 让焦点在回合开始时就落位，键盘用户需要 0 次 Tab。
   *
   * 三条边界（避免这次「帮忙」变成新的干扰）：
   *   ① 有浮层挂着（设置 / 历史 / 确认气泡）时绝不抢焦点；
   *   ② 用户自己把焦点放在行动区之外的控件上时不抢（那是用户本意）；
   *   ③ 落点选「跟注 / 过牌」这类可逆动作，绝不落在「弃牌」上，避免误触即弃牌。
   * 视觉不受影响：焦点环用 `:focus-visible`，鼠标玩家不会因此多出一圈高亮。
   */
  const wasMyTurn = useRef(false);
  useEffect(() => {
    const myTurn = !!legal && active;
    if (myTurn && !wasMyTurn.current && !overlayOpen()) {
      const dock = dockRef.current;
      const el = document.activeElement as HTMLElement | null;
      const idle = !el || el === document.body;
      const inBar = !!el && !!dock?.contains(el);
      if (dock && (idle || inBar)) {
        const primary =
          dock.querySelector<HTMLElement>('[data-act="check"]:not([disabled])') ??
          dock.querySelector<HTMLElement>('[data-act="call"]:not([disabled])') ??
          dock.querySelector<HTMLElement>('[data-act="sizer"]:not([disabled])');
        primary?.focus();
      }
    }
    wasMyTurn.current = myTurn;
  }, [active, legal]);

  /**
   * MAJ-F4（设计终验收 P0）：「全下」常驻主行动区。
   *
   * 第 2 轮为了防误触，把窄屏的「全下」下沉到了加注抽屉底部（`compact` 时 3 列）。
   * 设计终验收判这条为严重项：窄屏四个主行动只剩三个可直达，「全下」要先展开加注抽屉才能点到，
   * 叠加 BLK-F1 的短屏抽屉退化后几乎无法执行，窄屏也因此永远触发不到二次确认气泡。
   * 现在四个按钮在所有断点都留在主行动区，防误触改由**物理隔离 + 二次确认**承担：
   * 「弃牌」在最左、「全下」在最右，中间隔着跟注 / 加注两颗，且全下必过确认气泡。
   */
  const clockTotal = settings.actionClock * 1000;
  const clockRatio =
    clockRemainingMs !== null && clockTotal > 0 ? Math.max(0, Math.min(1, clockRemainingMs / clockTotal)) : null;
  const urgent = clockRemainingMs !== null && clockRemainingMs <= 5000;

  const allInButton = (
    <button
      type="button"
      className="btn act act--allin"
      data-act="allin"
      tabIndex={TAB_ALLIN}
      disabled={disabled || !legal?.allIn}
      /* AC-S4：金额口径统一为「本街累计投入」(to)。全下按钮显示 to（与加注/滑杆同口径），
         aria 显式标注「投入剩余筹码 chips」，消除「全下 1000 vs 你的筹码 679」的对不上。 */
      aria-label={
        legal?.allIn
          ? `全下：把本街累计投入加到 ${chips(legal.allIn.to)}，投入你剩余的全部筹码 ${chips(legal.allIn.chips)}`
          : '全下'
      }
      onClick={(e) => fire({ type: 'allin' }, e.currentTarget)}
    >
      全下 {legal?.allIn ? <span className="num">到 {chips(legal.allIn.to)}</span> : null} <kbd>A</kbd>
    </button>
  );

  /*
   * DEF-02：加注抽屉的层级承载。传入 sizerPortalRoot（窄屏 overlay 根）时把抽屉 portal 到
   * 平面 C（有效 z --z-eqfloat:110 > 公共牌带 40），越过 `.gs__foot` 的 isolate 封顶（z:20），
   * CTA 不再被英雄区元素打穿；宽屏 / 根未挂载时回退页脚内联渲染，几何与层级均不变。
   */
  const maybePortalSizer = (node: ReactElement): ReactNode =>
    sizerPortalRoot ? createPortal(node, sizerPortalRoot) : node;

  return (
    <section className={`actionbar${clockRatio !== null ? ' actionbar--clocked' : ''}`} aria-label="行动区">
      {/* MIN-10：行动时限在行动区顶部用 6px 进度条表达，剩余 ≤5s 转错误色 */}
      {clockRatio !== null ? (
        <div className={`actionbar__clock${urgent ? ' actionbar__clock--urgent' : ''}`} aria-hidden="true">
          <span className="actionbar__clock-bar" style={{ width: `${clockRatio * 100}%` }} />
        </div>
      ) : null}

      <div className="actionbar__info">
        {legal && toCall > 0 ? (
          <>
            <span className="ai-chip">
              需跟注 <b className="num">{chips(Math.min(toCall, myStack))}</b>
            </span>
            <span className="ai-chip">
              底池赔率 <b className="num">{potAfterCall > 0 ? pct(toCall / (pot + toCall)) : '—'}</b>
            </span>
          </>
        ) : legal ? (
          <span className="ai-chip">本街无人下注，可过牌</span>
        ) : null}
        <span className="ai-chip">
          你的筹码 <b className="num">{chips(myStack)}</b>
        </span>
        {clockRemainingMs !== null ? (
          <span className="ai-chip ai-chip--clock" role="timer">
            剩余 <b className="num">{Math.ceil(clockRemainingMs / 1000)}s</b>
          </span>
        ) : null}
        {disabled ? <span className="ai-chip ai-chip--muted">{disabledReason}</span> : null}
      </div>

      <div className="actionbar__dock pop-anchor" ref={dockRef}>
        <div className="actionbar__main">
          {/* T6 / AC-S1：唯一弃牌入口，三态。
              · 轮到我（inTurnFold）：危险色实心，「弃牌」，点击直接提交 fold（无二次确认）。
              · 非我轮已武装（canConcede）：降为次级（ghost + 虚线），「预选弃牌」，点击弹二次确认
                （文案不含「认输」），确认后置 pendingConcede。非我轮时**不是**视觉权重最高的元素。
              · 预选已置位（concedePending）：「取消预选」，点击清除尚未落地的意图（预选可取消）。
              随时可点（不退出 Tab 序）。 */}
          <button
            type="button"
            className={`btn act${
              inTurnFold ? ' btn--danger' : ' btn--ghost'
            }${canConcede ? ' act--concede' : ''}${concedePending ? ' act--preselect' : ''}`}
            data-act="fold"
            data-concede={canConcede ? '1' : undefined}
            data-preselect={concedePending ? '1' : undefined}
            tabIndex={TAB_FOLD}
            disabled={!foldClickable}
            aria-label={
              concedePending
                ? '取消预选弃牌'
                : canConcede
                  ? '预选弃牌：轮到你时自动弃掉本手（可取消）'
                  : undefined
            }
            onClick={(e) => {
              if (inTurnFold) fire({ type: 'fold' }, e.currentTarget);
              else if (concedePending) onCancelConcede?.();
              else if (canConcede) setConcedeConfirm(true);
            }}
          >
            {concedePending ? '取消预选' : canConcede ? '预选弃牌' : '弃牌'}{' '}
            {inTurnFold ? <kbd>F</kbd> : null}
          </button>

          {legal?.check ? (
            <button
              type="button"
              className="btn act"
              data-act="check"
              tabIndex={TAB_CALL}
              disabled={disabled}
              onClick={(e) => fire({ type: 'check' }, e.currentTarget)}
            >
              过牌 <kbd>C</kbd>
            </button>
          ) : (
            <button
              type="button"
              className="btn act"
              data-act="call"
              tabIndex={TAB_CALL}
              disabled={disabled || !legal?.call}
              onClick={(e) => fire({ type: 'call' }, e.currentTarget)}
            >
              跟注 {legal?.call ? <span className="num">{chips(legal.call.amount)}</span> : null}
              {legal?.call?.allIn ? '（全下）' : ''} <kbd>C</kbd>
            </button>
          )}

          {aggro ? (
            <button
              type="button"
              className={`btn btn--primary act${expanded ? ' act--on' : ''}`}
              data-act="sizer"
              tabIndex={TAB_AGGRO}
              disabled={disabled}
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {isRaise ? '加注' : '下注'} <kbd>R</kbd>
            </button>
          ) : (
            <button type="button" className="btn btn--primary act" tabIndex={TAB_AGGRO} disabled>
              {isRaise ? '加注' : '下注'}
            </button>
          )}

          {allInButton}
        </div>

        {/* R13-2：弃牌后二选一推进 —— 内联覆盖按钮行（非弹层），默认高亮「快进本手」。
            Esc / 条外点击 = 原速观战（在 FoldAdvancePrompt 里用 useDismissable 落到 normal）。 */}
        {foldPrompt ? (
          <FoldAdvancePrompt onChoose={(mode, remember) => onFoldAdvance?.(mode, remember)} />
        ) : null}

        {sizerOpen ? (
          /**
           * R14-1：加注面板恒含「快捷档 + 滑杆 + 数字输入 + 确认 CTA」四件（缺一不可）。
           *
           * 过去方案 B 在 `short` 档丢弃滑杆/数字输入，移动端只剩 3 档圆钮，无法自定义任意加注额。
           * 本轮把「是否有滑杆/输入」从模式阶梯里剥离：`full`/`compact`/`short` 三档都渲染全件，
           * 模式**只决定排版疏密**（样式层：full 多行 / compact 紧凑 / 横屏收成单行），短高度靠
           * 浮层内部滚动兜底（`.sizer{overflow-y:auto}`）。移动端两目标已被 pickSizerMode(mobileFloor)
           * 封顶在 compact，因此 430×932 / 932×430 恒为 compact、绝不落 short。
           *
           * 指针路径：点档位 / 拖滑杆 / 键入数字 → `amount`（snapAmount 规整到 [min,max]）→ 点 CTA 提交；
           * amount≥max 且可全下 → 走 allin（经既有二次确认气泡，PRD 7.6 不放宽）。
           *
           * DEF-02：窄屏经 maybePortalSizer 提到 overlay 根（加 `sizer--portal` 走视口底定位 + 提层）。
           */
          maybePortalSizer(
            <div
              className={`sizer sizer--${sizerMode}${sizerPortalRoot ? ' sizer--portal' : ''}${heroMirror ? ' sizer--mirror' : ''}`}
              role="group"
              aria-label={isRaise ? '加注金额' : '下注金额'}
            >
            {/* Round26 · C3（问题1 · 方案 A）：窄带无纯几何解时，面板顶部固定镜像 Hero 两张底牌。
                flex:none 永不随内部滚动移出，保证下注全程「看得见自己的牌」（AC-B3 visFrac=1）。 */}
            {heroMirror && heroHole && heroHole.length >= 2 ? (
              <div className="sizer__mirror" aria-label="你的手牌">
                <span className="sizer__mirror-label">你的手牌</span>
                <PlayingCard card={heroHole[0]} size="sm" />
                <PlayingCard card={heroHole[1]} size="sm" />
              </div>
            ) : null}
            <div className="sizer__quick">
              {quick.map((q) => (
                <button
                  key={q.ratio}
                  type="button"
                  className={`chipbtn${amount === q.to ? ' chipbtn--on' : ''}`}
                  tabIndex={TAB_SIZER}
                  aria-label={q.label}
                  aria-pressed={amount === q.to}
                  onClick={() => setAmount(q.to)}
                >
                  {q.head}
                  <span className="chipamt__unit"> {q.unit}</span>
                  {/* AC-S3：档位带基准——除倍数外显式给出该档「到 X」的结果金额（如「底池50% · 到 275」）。 */}
                  <span className="chipamt__to num">到 {chips(q.to)}</span>
                </button>
              ))}
              <button
                type="button"
                className={`chipbtn${amount === min ? ' chipbtn--on' : ''}`}
                tabIndex={TAB_SIZER}
                aria-label="最小"
                aria-pressed={amount === min}
                onClick={() => setAmount(min)}
              >
                最小
                <span className="chipamt__to num">到 {chips(min)}</span>
              </button>
              <button
                type="button"
                className={`chipbtn${amount === max ? ' chipbtn--on' : ''}`}
                tabIndex={TAB_SIZER}
                aria-label="最大"
                aria-pressed={amount === max}
                onClick={() => setAmount(max)}
              >
                最大
                <span className="chipamt__to num">到 {chips(max)}</span>
              </button>
            </div>
            {/* AC-S3 / R21-M5 BUG-06：滑杆**独占整行且撑满抽屉内宽**（360 屏抽屉内宽 ≈312 → 滑杆
                ≥240 达标；改前 min/max 标注与滑杆同处一行争抢宽度，360 实测滑杆仅 232.4<240）。
                两端最小/最大值改到滑杆下方的刻度行 .sizer__scale（基准仍可读，AC-S3 不回退）。 */}
            <div className="sizer__sliderrow">
              <input
                className="sizer__slider"
                type="range"
                min={min}
                max={max}
                step={bigBlind}
                value={amount}
                tabIndex={TAB_SIZER}
                aria-label={`${isRaise ? '加注至' : '下注至'}（${min} 到 ${max}，步进 ${bigBlind}）`}
                onChange={(e) => setAmount(snapAmount(Number(e.target.value), min, max, bigBlind))}
                onKeyDown={(e) => {
                  // 原生 range 的 Shift+←→ 与 ←→ 等效，这里补上设计要求的「十步」档
                  if (!e.shiftKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
                  e.preventDefault();
                  const delta = (e.key === 'ArrowRight' ? bigBlind : -bigBlind) * 10;
                  setAmount((v) => snapAmount(v + delta, min, max, bigBlind));
                }}
              />
            </div>
            <div className="sizer__scale" aria-hidden="true">
              <span className="sizer__end num">{chips(min)}</span>
              <span className="sizer__end num">{chips(max)}</span>
            </div>
            {/* Round26 · C3：说明行移到 CTA 行之上，让 `.sizer__row`（取消 / 确认）成为面板最后一个子节点，
                sticky bottom:0 恒贴面板底、随内部滚动始终完整可见（AC-B1/B2 CTA visFrac=1，不被下方提示压）。 */}
            <p className="sizer__hint">
              金额为「本街累计投入」（to 语义）：本次实际放入 <span className="num">{chips(Math.max(0, amount - myStreetContributed))}</span>
              ，区间 <span className="num">{chips(min)}</span>–<span className="num">{chips(max)}</span>，步进{' '}
              <span className="num">{chips(bigBlind)}</span>（←→ 单步，Shift+←→ 十步）
              {max === (legal?.allIn?.to ?? -1) ? '（上限即全下）' : ''}
            </p>
            <div className="sizer__row">
              <input
                className="input input--num sizer__input"
                type="number"
                min={min}
                max={max}
                step={bigBlind}
                value={amount}
                tabIndex={TAB_SIZER}
                aria-label="金额输入"
                onChange={(e) => setAmount(snapAmount(Number(e.target.value) || 0, min, max, bigBlind))}
              />
              {/* AC-S3：显式「取消」——收起加注面板、回到行动栏，不提交任何动作。 */}
              <button
                type="button"
                className="btn btn--ghost sizer__cancel"
                data-act="sizer-cancel"
                tabIndex={TAB_SIZER}
                aria-label="取消加注，返回行动栏"
                onClick={() => setExpanded(false)}
              >
                取消
              </button>
              <button
                type="button"
                className="btn btn--primary sizer__cta"
                data-act="raise"
                tabIndex={TAB_SIZER}
                aria-label={`确认${isRaise ? '加注至' : '下注'} ${chips(amount)}（范围 ${chips(min)} 到 ${chips(max)}，步进 ${chips(bigBlind)}）`}
                onClick={(e) =>
                  fire(amount >= max && legal?.allIn ? { type: 'allin' } : { type: isRaise ? 'raise' : 'bet', amount }, e.currentTarget)
                }
              >
                {/* Round2 问题5：文案由「确认加注至 220」收敛到「确认 · 220」（≈4 字符 + 金额），
                    窄屏 CTA 不再被撑宽超框；to 语义（加注至）完整保留在 aria-label（读屏不受影响）。 */}
                确认 · <span className="num">{chips(amount)}</span>
              </button>
            </div>
          </div>,
          )
        ) : null}

        {/* T6 / AC-S1：越轮弃牌的二次确认气泡（锚定在「弃牌」按钮上方，防误触）。文案不含「认输」。
            确认后走 controller.concedeAnytime（置 pendingConcede，轮到你时自动弃掉本手，可取消）。 */}
        <ConfirmPopover
          open={concedeConfirm}
          label="预选弃牌？轮到你时自动弃掉本手。"
          anchorX={anchorXOf(dockRef.current?.querySelector<HTMLElement>('[data-act="fold"]') ?? null)}
          anchorEl={dockRef.current?.querySelector<HTMLElement>('[data-act="fold"]') ?? null}
          confirmText="预选弃牌"
          cancelText="返回"
          onCancel={() => setConcedeConfirm(false)}
          onConfirm={() => {
            setConcedeConfirm(false);
            onConcede?.();
          }}
        >
          此刻不轮到你。确认后将在轮到你时自动弃掉本手，期间可随时「取消预选」。
        </ConfirmPopover>

        {/* MAJ-5：二次确认改为锚定在触发按钮上方 8px 的气泡，不再是居中 Modal */}
        <ConfirmPopover
          open={!!confirm}
          label="确认这笔投入"
          anchorX={confirm?.anchorX ?? null}
          anchorEl={confirm?.anchorEl ?? null}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            const a = confirm?.action;
            setConfirm(null);
            if (a) onSubmit(a);
          }}
        >
          {confirm?.action.type === 'allin' ? (
            <>
              将投入全部剩余筹码 <b className="num">{chips(myStack)}</b>，本手可能因此结束。
            </>
          ) : confirm?.action.type === 'call' ? (
            <>
              跟注需要 <b className="num">{chips(myStack)}</b>，即你的全部剩余筹码，本手可能因此结束。
            </>
          ) : (
            <>
              本次将放入 <b className="num">{chips(Math.max(0, (confirm?.action.amount ?? 0) - myStreetContributed))}</b>
              ，超过你剩余筹码的一半（剩余 <b className="num">{chips(myStack)}</b>）。
            </>
          )}
        </ConfirmPopover>
      </div>
    </section>
  );
}

/**
 * R13-2：弃牌后二选一推进浮层（内联覆盖按钮行）。
 *
 * 为什么不是 Modal / 弹层：弃牌后视线仍停在行动区，这里就地把按钮行换成
 * 「⏩ 快进本手 / 👁 原速观战 / ☑ 记住」，不打断视线也不遮挡牌桌席位（B1-B5）。
 * 默认焦点落在「快进本手」（primary 高亮，autoFocus）。
 * Esc / 条外点击 = 原速观战：useDismissable 的 onClose 走 normal，与设计一致。
 * 「记住」勾选后由 controller 写入 settings.foldAdvanceMode 并关闭「每次询问」。
 */
function FoldAdvancePrompt({ onChoose }: { onChoose: (mode: 'turbo' | 'normal', remember: boolean) => void }) {
  const [remember, setRemember] = useState(false);
  const rememberRef = useRef(remember);
  rememberRef.current = remember;
  const ref = useDismissable(true, () => onChoose('normal', rememberRef.current));
  return (
    <div className="postfold" role="group" aria-label="弃牌后推进方式" ref={ref}>
      <button
        type="button"
        className="btn btn--primary pf-primary"
        data-pf="turbo"
        autoFocus
        onClick={() => onChoose('turbo', remember)}
      >
        ⏩ 快进本手
      </button>
      <button
        type="button"
        className="btn btn--ghost pf-ghost"
        data-pf="normal"
        onClick={() => onChoose('normal', remember)}
      >
        👁 原速观战
      </button>
      <label className="pf-remember">
        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.currentTarget.checked)} />
        <span>记住</span>
      </label>
    </div>
  );
}

function clamp(v: number, min: number, max: number): number {
  if (!Number.isFinite(v)) return min;
  return Math.min(max, Math.max(min, Math.round(v)));
}

/**
 * snapAmount —— 把任意输入规整到「大盲步进」的网格上（设计方案 4.5：步进 = 大盲）
 *
 * 网格锚点取 `min`（引擎给出的最小加注）而不是 0：从合法下限起跳，网格上的每一档都必然合法，
 * 也保证「最小加注」这一最常用的值精确可达。
 *
 * 两个边界必须精确可达，因此不参与规整：
 *   - `min`：最小加注（可能不是大盲整数倍，例如面对非标准加注后的重加注下限）；
 *   - `max`：全下额度（等于剩余筹码，几乎不会落在网格上）。
 * 当规整结果越过 `max` 时收敛到 `max`，避免出现「差几个筹码摸不到全下」的死角。
 */
function snapAmount(v: number, min: number, max: number, step: number): number {
  if (!Number.isFinite(v)) return min;
  const raw = Math.round(v);
  if (raw <= min) return min;
  if (raw >= max) return max;
  if (step <= 0) return raw;
  const grid = min + Math.round((raw - min) / step) * step;
  if (grid >= max) return max;
  return grid <= min ? min : grid;
}

/**
 * 快捷档标签拆成「倍数 + 单位」两段。
 *
 * BLK-F1：拆开是为了让样式层能在短屏（≤820px 高）只隐去单位词「底池」。
 * 实测 360×640：整标签渲染下 6 个快捷档要 380px，超出抽屉内宽 324px 会折成两行、
 * 快捷档区从 44 长到 92px，抽屉内容需求 162px > 可用 156px，于是三件控件被迫内滚。
 * 去掉单位词后 6 个档位实测 272px，单行放得下，内容需求降到 114px。
 *
 * 可访问名不受影响：按钮上挂 `aria-label={label}`（完整标签），
 * 读屏与既有用例（`getByRole('button', { name: '1× 底池' })`）拿到的都还是完整文案。
 */
function ratioLabel(r: number): { label: string; head: string; unit: string } {
  const head = r === 1 ? '1×' : r === 2 ? '2×' : `${Math.round(r * 100)}%`;
  return { label: `${head} 底池`, head, unit: '底池' };
}
